import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '@/shared/lib/cn';

import { Icon } from './Icon';
import { ToastContext, type ToastApi, type ToastTone } from './toastContext';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

/**
 * Avisos curtos de resultado de acao.
 *
 * Uma acao que muda dado precisa confirmar que mudou. Sem retorno, a pessoa repete
 * a operacao para ter certeza, e a repeticao e justamente o que nao se quer em
 * cadastro e em transicao de etapa.
 *
 * O aviso e anunciado por regiao viva, para que quem usa leitor de tela receba o
 * mesmo retorno de quem enxerga a tela.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  // Os temporizadores que tiram cada aviso da tela. Saindo da tela, eles sao
  // cancelados: sem isso, o aviso some depois de a tela nao existir mais, e o
  // temporizador alcanca um ambiente que ja foi desfeito.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const pendentes = timers.current;
    return () => {
      pendentes.forEach((timer) => clearTimeout(timer));
      pendentes.clear();
    };
  }, []);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, tone, message }]);
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 5000);
    timers.current.add(timer);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              // A largura acompanha o texto: a mensagem fica em uma linha quando cabe, e so
              // quebra quando a tela e estreita.
              'pointer-events-auto flex w-fit max-w-full items-start gap-2.5 sm:max-w-3xl',
              'rounded-xl border px-3.5 py-2.5 text-sm shadow-(--shadow-lg)',
              'animate-[fade-in_var(--duration-base)_ease-out]',
              toast.tone === 'success' &&
                'border-success-200 bg-success-50 text-success-700 shadow-[inset_3px_0_0_var(--color-success-500),var(--shadow-lg)]',
              toast.tone === 'error' &&
                'border-danger-200 bg-danger-50 text-danger-700 shadow-[inset_3px_0_0_var(--color-danger-500),var(--shadow-lg)]',
              toast.tone === 'info' &&
                'border-line bg-surface text-ink-700 shadow-[inset_3px_0_0_var(--color-brand-500),var(--shadow-lg)]',
            )}
          >
            <Icon
              name={
                toast.tone === 'error'
                  ? 'alert'
                  : toast.tone === 'info'
                    ? 'info'
                    : 'check'
              }
              size={15}
              className="mt-0.5 shrink-0"
            />
            <p className="min-w-0">{toast.message}</p>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
